import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../ui-components/ui/card';
import { TrendingUp, Users, Phone, FileText, Trophy, X } from 'lucide-react';

interface FunnelStage {
  name: string;
  icon: React.ReactNode;
  count: number;
  value: number;
  color: string;
  conversionRate?: number;
}

const FunnelVisualization: React.FC = () => {
  const stages: FunnelStage[] = [
    {
      name: 'New Leads',
      icon: <Users className="h-4 w-4" />,
      count: 248,
      value: 248000,
      color: 'bg-blue-500',
    },
    {
      name: 'Contacting',
      icon: <Phone className="h-4 w-4" />,
      count: 189,
      value: 189000,
      color: 'bg-cyan-500',
      conversionRate: 76.2,
    },
    {
      name: 'Qualified',
      icon: <FileText className="h-4 w-4" />,
      count: 124,
      value: 372000,
      color: 'bg-purple-500',
      conversionRate: 65.6,
    },
    {
      name: 'Offer',
      icon: <TrendingUp className="h-4 w-4" />,
      count: 67,
      value: 536000,
      color: 'bg-orange-500',
      conversionRate: 54.0,
    },
    {
      name: 'Won',
      icon: <Trophy className="h-4 w-4" />,
      count: 28,
      value: 448000,
      color: 'bg-green-500',
      conversionRate: 41.8,
    },
  ];

  const maxCount = Math.max(...stages.map(s => s.count));

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle>Sales Funnel</CardTitle>
        <CardDescription>Deal-Fortschritt diese Woche</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {stages.map((stage, index) => {
            const widthPercentage = (stage.count / maxCount) * 100;
            
            return (
              <div key={stage.name} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className={`p-1.5 rounded ${stage.color} text-white`}>
                      {stage.icon}
                    </div>
                    <span className="font-medium">{stage.name}</span>
                    {stage.conversionRate && (
                      <span className="text-xs text-gray-500">
                        ({stage.conversionRate}% CR)
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-bold">{stage.count}</span>
                    <span className="text-gray-500 text-xs">
                      €{(stage.value / 1000).toFixed(0)}k
                    </span>
                  </div>
                </div>
                
                <div className="relative">
                  <div className="h-8 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${stage.color} transition-all duration-500 ease-out rounded-full`}
                      style={{ width: `${widthPercentage}%` }}
                    />
                  </div>
                  {index < stages.length - 1 && (
                    <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2">
                      <div className="h-6 w-px bg-gray-300" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Lost Deals */}
        <div className="mt-6 pt-4 border-t">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded bg-red-500 text-white">
                <X className="h-4 w-4" />
              </div>
              <span className="font-medium">Lost</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-bold">39</span>
              <span className="text-gray-500 text-xs">€234k</span>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Gesamt Pipeline</p>
              <p className="font-bold text-lg">€1.79M</p>
            </div>
            <div>
              <p className="text-gray-500">Conversion Rate</p>
              <p className="font-bold text-lg">11.3%</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default FunnelVisualization;
